/** Portable document contract; bytes and download URLs never come from the model. */
import { RECORD_ID_RE } from '../../api/_lib/record-id.js';
export interface FactuurDocument {
  versie: 1;
  invoiceId: string;
  accountId: string;
  referentie: string;
  ontvanger: string;
  bronHash: string;
  pdfHash: string;
  bestandsnaam: string;
  mimeType: 'application/pdf';
  grootte: number;
}

/**
 * Bijlagen reizen als base64 in de JSON-body en Vercel kapt elke request op 4,5 MB,
 * ongeacht `bodyParser.sizeLimit` (dat is een Next-conventie die de functie niet
 * krijgt). 3 MB ruw is ~4 MB base64 en blijft eronder; daarboven krijgt kantoor een
 * kale HTTP 413 zonder audit-rij (WB-323818, werkbank #40).
 */
export const MAX_MAIL_BIJLAGEN_BYTES = 3 * 1024 * 1024;
export const MAX_MAIL_BIJLAGEN_LABEL = '3 MB';
export const MAX_FACTUUR_DOCUMENTEN = 10;
const HASH = /^[a-f0-9]{64}$/;
const ID = RECORD_ID_RE;

export function leesFactuurDocumenten(raw: unknown): FactuurDocument[] | null {
  if (!Array.isArray(raw) || !raw.length || raw.length > MAX_FACTUUR_DOCUMENTEN) return null;
  const ids = new Set<string>();
  const uit: FactuurDocument[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object' || r.versie !== 1 || !ID.test(r.invoiceId || '') || !ID.test(r.accountId || '')
      || typeof r.referentie !== 'string' || !/^Inv-\d{1,10}$/i.test(r.referentie)
      || typeof r.ontvanger !== 'string' || !/^[^\s<>@,;]+@[^\s<>@,;]+\.[a-z]{2,}$/i.test(r.ontvanger)
      || !HASH.test(r.bronHash || '') || !HASH.test(r.pdfHash || '')
      || r.mimeType !== 'application/pdf' || typeof r.bestandsnaam !== 'string'
      || !/^(?:Creditfactuur|Factuur)_Inv-\d{1,10}\.pdf$/i.test(r.bestandsnaam)
      || !Number.isSafeInteger(r.grootte) || r.grootte <= 0 || r.grootte > MAX_MAIL_BIJLAGEN_BYTES
      || ids.has(r.invoiceId)) return null;
    ids.add(r.invoiceId);
    uit.push({ versie: 1, invoiceId: r.invoiceId, accountId: r.accountId, referentie: r.referentie,
      ontvanger: r.ontvanger, bronHash: r.bronHash, pdfHash: r.pdfHash, bestandsnaam: r.bestandsnaam,
      mimeType: 'application/pdf', grootte: r.grootte });
  }
  if (uit.reduce((n, d) => n + d.grootte, 0) > MAX_MAIL_BIJLAGEN_BYTES) return null;
  return uit;
}

export function factuurDocumentSleutel(d: FactuurDocument): string { return `${d.invoiceId}:${d.bronHash}:${d.pdfHash}`; }

/** Throw on broken persisted evidence, never silently drop an approved attachment. */
export function facturenUitActies(raw: unknown): FactuurDocument[] {
  if (!Array.isArray(raw)) return [];
  const uit = new Map<string, FactuurDocument>();
  for (const a of raw) {
    if (a?.verb !== 'bereid_factuurconcept' || a.uitkomst?.besluit !== 'akkoord' || a.uitkomst.ok !== true) continue;
    const docs = leesFactuurDocumenten(a.uitkomst.documenten);
    if (!docs) throw new Error('Het factuurbewijs is onvolledig. Bereid de facturen opnieuw voor voordat je verstuurt.');
    for (const d of docs) {
      const oud = uit.get(d.invoiceId);
      if (oud && factuurDocumentSleutel(oud) !== factuurDocumentSleutel(d)) throw new Error('Twee versies van dezelfde factuur geselecteerd. Controleer het concept.');
      uit.set(d.invoiceId, d);
    }
  }
  const docs = [...uit.values()];
  if (docs.length && !leesFactuurDocumenten(docs)) throw new Error('De geselecteerde facturen overschrijden de bijlagenlimiet.');
  return docs;
}
